import { useTranslation } from "react-i18next";
import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Modal, Row, Select, TableColumnsType, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { getAllTeacherAndFacility } from '../../service/library/question-list';
import { AuthorizedUserType } from '../../types/listTrainingNeeds';
import { permissionOptions } from '../../utils/sharePermission';
import FormItemDropdown from '../form-dropdown/FormItemDropdown';
import TableData from '../table-data/TableData';
import "./AddUserPermissionModal.scss";

const { Option } = Select;

interface Props {
	open: boolean;
	authorizedUsers: any[];
	onCancel: () => void;
	onOk: (users: { email: string; name: string; permission: string }[]) => void;
}
const { Search } = Input;

const AddUserPermissionModal: React.FC<Props> = ({ open, onCancel, onOk, authorizedUsers }) => {
  const { t } = useTranslation();
	const [dataTable, setDataTable] = useState<AuthorizedUserType[]>([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalRecords, setTotalRecords] = useState(0);
	const [selectedRows, setSelectedRows] = useState<AuthorizedUserType[]>([]);
	const [permission, setPermission] = useState('2');
	const [loading, setLoading] = useState(false);
	const [keyword, setKeyword] = useState('');

	const authorizedEmails = authorizedUsers?.map(u => u.email) || [];

	const fetchData = async () => {
		setLoading(true);
		try {
			const res = await getAllTeacherAndFacility({
				page: pageNumber,
				size: pageSize,
				keyword
			});
			const uniq = new Map();
			res?.data?.data?.content?.forEach((u: AuthorizedUserType) => {
				if (u.email && !uniq.has(u.email)) {
					uniq.set(u.email, u);
				}
			});
			setDataTable(Array.from(uniq.values()));
			setTotalRecords(res?.data?.data?.totalElements || 0);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			fetchData();
		}
	}, [open, pageNumber, pageSize]);

	useEffect(() => {
		if (open) {
			setPageNumber(1);
			setPermission('2');
		}
		setKeyword("")

	}, [open]);

	useEffect(() => {
		if (open) {
			const authorizedRowsData = dataTable.filter(user => isUserAuthorized(user));
			setSelectedRows(authorizedRowsData);
		}
	}, [open]);

	const handleOk = () => {
		const newSelectedUsers = selectedRows.filter(u => !isUserAuthorized(u));
		if (newSelectedUsers.length && permission) {
			const users = newSelectedUsers.map((u) => ({
				email: u.email,
				name: u.name,
				permission,
			}));
			onOk(users);
		}
	};

	const handleCancel = () => {
		setSelectedRows([]);
		setPermission('2');
		onCancel();
	};

	const isUserAuthorized = (record: AuthorizedUserType) => {
		return authorizedEmails.includes(record.email);
	};

	const disableRowCheckbox = (record: AuthorizedUserType) => {
		return isUserAuthorized(record);
	};

	const handleRowSelection = (rows: AuthorizedUserType[]) => {
		const authorizedInSelection = rows.filter(r => isUserAuthorized(r));
		const nonAuthorizedInSelection = rows.filter(r => !isUserAuthorized(r));

		const currentAuthorizedUsers = dataTable.filter(user => isUserAuthorized(user));
		const finalSelection = [...currentAuthorizedUsers, ...nonAuthorizedInSelection];
		const uniqueSelection = finalSelection.filter((item, index, self) =>
			index === self.findIndex((t) => t.id === item.id)
		);

		setSelectedRows(uniqueSelection);
	};

	const dataColumns: TableColumnsType<any> = [
		{
			title: "STT",
			dataIndex: "numericalOrder",
			key: "numericalOrder",
			width: 60,
			render: (_: any, record) => {
				return (
					<span className="table-order-header">{record?.numericalOrder}</span>
				);
			},
		},
		{
			title: t("Giảng viên", "Giảng viên"),
			dataIndex: 'name',
			key: 'name',
			render: (_: any, record: AuthorizedUserType) => {
				const isDisabled = isUserAuthorized(record);
				return (
					<div style={{ opacity: isDisabled ? 0.5 : 1 }}>
						<strong>{record.name}</strong>
						<br />
						<span style={{ color: '#888' }}>{record.email}</span>
						{isDisabled && (
							<div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
								{t("Đã được chia sẻ", "Đã được chia sẻ")}
							</div>
						)}
					</div>
				);
			},
		},
	];

	const availableCount = dataTable.filter(user => !isUserAuthorized(user)).length;
	const newSelectedCount = selectedRows.filter(user => !isUserAuthorized(user)).length;

	const handleSearch = () => {
		setPageNumber(1);
		fetchData();
	};

	return (
		<Modal
			open={open}
			title={t("Thêm người chia sẻ", "Thêm người chia sẻ")}
			onCancel={handleCancel}
			onOk={handleOk}
			okText={t("Thêm", "Thêm")}
			cancelText={t("Huỷ", "Huỷ")}
			width={1000}
			className="add-user-permission-modal"
			centered
			okButtonProps={{
				disabled: newSelectedCount === 0
			}}
		>
			<Row gutter={16} style={{ marginBottom: 16 }}>
				<Col span={12}>
					<Search
						placeholder={t("Tìm kiếm theo email hoặc tên", "Tìm kiếm theo email hoặc tên")}
						value={keyword}
						onChange={(e) => setKeyword(e.target.value)}
						onSearch={handleSearch}
						onPressEnter={handleSearch}
						style={{ width: '100%' }}
						allowClear
						enterButton={
							<Button
								icon={<SearchOutlined />}
								loading={loading}
							>
							</Button>
						}
					/>
				</Col>

				<Col span={12}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<Typography.Text>{t("Quyền mặc định:", "Quyền mặc định:")}</Typography.Text>
						<FormItemDropdown
							description
							value={permission}
							onChange={(val) => setPermission(val)}
							options={permissionOptions
								.filter((p) => p.id === '2' || p.id === '3')}
							style={{ width: "200px" }}
						/>
					</div>
				</Col>
			</Row>

			<div style={{ marginBottom: 8 }}>
				<strong>{t("Chọn giảng viên", "Chọn giảng viên")}</strong>
				{newSelectedCount > 0 && (
					<span style={{ marginLeft: 8, color: '#1890ff' }}>
						({newSelectedCount} {t("người mới được chọn)", "người mới được chọn)")}
					</span>
				)}
				<span style={{ float: 'right', fontSize: 13, color: '#888' }}>
					{availableCount} {t("người khả dụng", "người khả dụng")}
				</span>
			</div>

			<TableData
				dataTable={dataTable}
				dataColumns={dataColumns}
				totalRecords={totalRecords}
				pageNumber={pageNumber}
				pageSize={pageSize}
				pagination={true}
				setPageNumber={setPageNumber}
				setPageSize={setPageSize}
				rowSelected={selectedRows}
				setSelectedRowKeysProps={handleRowSelection}
				handleTableChangeProps={() => { }}
				unit={t("giảng viên", "giảng viên")}
				hideSelection={true}
				loadingTable={loading}
				scroll={{ x: 700, y: 400 }}
				disableRowCheckbox={disableRowCheckbox}
				clearRowkey={!open}
				hideDisabledCheckbox={false}
			/>
		</Modal>
	);
};

export default AddUserPermissionModal;